import { NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { IconHome, IconClock, IconGrid, IconGear, IconLock } from './Icons';
import { useExamMode } from '../context/ExamModeContext';

// Bottom nav lifted from the MamaMind app shell pattern and adapted for the
// My Dyslexia Tools portfolio. Fixed, max-width 460 px, frosted glass, thin
// SVG icons. Five tabs would be too many for v1 (single tool) so we ship four.
//
// EXAM MODE behaviour: when an attempt is active the regular nav (History,
// Tools, Settings) is replaced with a single "End attempt" link, so the
// candidate has exactly one way out of the locked flow and cannot accidentally
// hop to Settings or Tools mid-paper. This is a JCQ requirement.

interface Item {
  to: string;
  label: string;
  Icon: (props: { size?: number; className?: string }) => ReactNode;
}

const ITEMS: Item[] = [
  { to: '/app',          label: 'Home',     Icon: IconHome },
  { to: '/app/history',  label: 'History',  Icon: IconClock },
  { to: '/app/tools',    label: 'Tools',    Icon: IconGrid },
  { to: '/app/settings', label: 'Settings', Icon: IconGear },
];

const EXAM_MODE_ITEMS: Item[] = [
  { to: '/app',          label: 'Attempt',  Icon: IconLock },
  { to: '/app/exam/end', label: 'End',      Icon: IconLock },
];

export function BottomNav() {
  const location = useLocation();
  const { active: examModeActive } = useExamMode();
  if (!location.pathname.startsWith('/app')) return null;

  // In Exam Mode the four-tab nav is replaced with a slim two-item nav.
  // This stops the candidate from navigating to History / Tools / Settings
  // while the attempt is live.
  const items = examModeActive ? EXAM_MODE_ITEMS : ITEMS;

  return (
    <nav
      aria-label="Primary"
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[460px] z-30"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div
        className={`mx-2 mb-2 rounded-2xl backdrop-blur-md shadow-lg shadow-ink/5 ${
          examModeActive
            ? 'bg-red-50/95 border border-red-300'
            : 'bg-white/85 border border-ink/5'
        }`}
      >
        <ul className="flex items-stretch justify-around">
          {items.map((item) => {
            const Icon = item.Icon;
            return (
              <li key={item.to} className="flex-1">
                <NavLink
                  to={item.to}
                  end={item.to === '/app'}
                  className={({ isActive }) =>
                    [
                      'flex flex-col items-center justify-center gap-1 py-2.5 transition',
                      examModeActive
                        ? isActive
                          ? 'text-red-800'
                          : 'text-red-700/70 hover:text-red-800'
                        : isActive
                          ? 'text-accent'
                          : 'text-ink/50 hover:text-ink/80',
                    ].join(' ')
                  }
                >
                  <Icon size={22} />
                  <span className="text-[10px] tracking-wide">{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
